<script setup lang="ts">
import { useAppStore } from "@/store/app";
import { useFieldSelection } from "@/composition/useFieldSelection";
import { useSurveyDragDropOperations } from "@/composition/builder/useSurveyDragDropOperations";
import { getFieldTypeIcon } from "@/util/fieldTypeIcon";
import {
  ChevronDownIcon,
  PlusIcon,
  SearchSmIcon,
  XCloseIcon,
} from "@gohighlevel/ghl-icons/24/outline";
import {
  UICheckbox,
  UICollapse,
  UICollapseItem,
  UIInput,
  UISelect,
} from "@gohighlevel/ghl-ui";
import { truncate } from "lodash";
import { storeToRefs } from "pinia";
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { VueDraggableNext } from "vue-draggable-next";
import { useI18n } from "vue-i18n";

const store = useAppStore();
// Invariant-safe drop entrypoint for survey/quiz slides. The canvas uses the
// same composable, so bulk inserts go through the same normalization
// (cloneDeep, uuid, rating config, contact-association modal, custom-object
// validation, history) as an interactive drag drop.
const surveyDragOps = useSurveyDragDropOperations();

const { t } = useI18n();
const { getCustomFieldsFromStore, getCustomFieldFoldersFromStore, getCustomObjectsFromStore } =
  storeToRefs(store);
const searchTerm = ref("");

// Dynamic options that will be populated from store
const customFieldsOptions = ref([]);
const customFieldsType = ref("contact");

// Exclude specific standard opportunity fields by fieldKey
const EXCLUDED_OPPORTUNITY_FIELD_KEYS = new Set([
  "opportunity.pipeline_id",
  "opportunity.pipeline_stage_id",
  "opportunity.status",
  "opportunity.monetary_value",
]);

// Exclude field types that cannot be filled in by a respondent, so they are
// never draggable onto the canvas. FORMULA fields are computed server-side
// from other field values. They stay in the store, so they remain available
// as merge variables and to uniqueness validation elsewhere in the builder.
const EXCLUDED_CUSTOM_FIELD_DATA_TYPES = new Set<string>(["FORMULA"]);

/**
 * Update dropdown options from store data
 * Sort objects by priority: contact, opportunity, business, then remaining
 */
const updateDropdownOptions = () => {
  const objectsRaw = getCustomObjectsFromStore.value || [];
  // If custom objects feature is disabled, allow only Contact and Opportunity
  const objects = store.isCustomObjectEnabled
    ? objectsRaw
    : objectsRaw.filter((o: any) => o?.key === "contact" || o?.key === "opportunity");
  const priorityOrder = ["contact", "opportunity", "business"];

  // Sort objects by priority index (non-priority items get high index)
  const sortedObjects = [...objects].sort((a, b) => {
    const getPriority = (key) =>
      priorityOrder.indexOf(key) !== -1 ? priorityOrder.indexOf(key) : 999;
    return getPriority(a.key) - getPriority(b.key);
  });

  customFieldsOptions.value = sortedObjects.map((obj) => ({
    label: obj.labels.singular,
    value: obj.key,
  }));
};

const filterByType = (fields) =>
  fields.filter((customField) => customField.model === customFieldsType.value);
const customFieldsCollection = ref(filterByType(getCustomFieldsFromStore.value));
const customFoldersCollection = ref(filterByType(getCustomFieldFoldersFromStore.value));
interface FieldGroup {
  name: string;
  fields: any[];
  showCount: number;
}
const groupedCustomFields = ref<FieldGroup[]>([]);

const openCustomFieldModal = () => {
  store.openCustomFieldsModel(customFieldsType.value);
};

const isSearching = computed<boolean>(() => searchTerm.value.trim().length > 0);
const hasNoFieldsToShow = computed<boolean>(() => groupedCustomFields.value.length === 0);
const defaultExpandedGroup = computed<string[]>(() =>
  groupedCustomFields.value.length > 0 ? [groupedCustomFields.value[0].name] : []
);

const clearSearch = (): void => {
  searchTerm.value = "";
  updateCustomFieldsOptions();
};

const updateCustomFieldsOptions = () => {
  customFieldsCollection.value = filterByType(getCustomFieldsFromStore.value);
  customFoldersCollection.value = filterByType(getCustomFieldFoldersFromStore.value);

  groupedCustomFields.value = customFoldersCollection.value
    .map((folder) => ({
      name: folder.name,
      fields: customFieldsCollection.value
        .filter((field) => field.parentId === folder._id)
        .filter((field) => {
          // Remove non-fillable field types from the left sidebar, whatever the model
          if (EXCLUDED_CUSTOM_FIELD_DATA_TYPES.has(field?.dataType)) {
            return false;
          }
          if (field?.model === "opportunity") {
            // Remove all Opportunity standard fields from the left sidebar
            if (isStandardField(field)) {
              return false;
            }
            return !EXCLUDED_OPPORTUNITY_FIELD_KEYS.has(field?.fieldKey);
          }
          return true;
        }),
      showCount: 10,
    }))
    .map((group) => ({
      ...group,
      fields: group.fields.filter((field) =>
        field.name.toLowerCase().includes(searchTerm.value.toLowerCase())
      ),
    }))
    .filter((group) => group.fields.length > 0);
};
const showMore = (group) => {
  const index = groupedCustomFields.value.indexOf(group);
  if (index !== -1) {
    groupedCustomFields.value[index].showCount += 10;
  }
};

// Disable dragging for standard fields that are already available in forms
const isStandardField = (field: any): boolean => {
  if (!field) {
    return false;
  }
  return (
    field?.dataType === "STANDARD_FIELD" ||
    field?.type === "standard_field" ||
    field?.standard === true
  );
};

// Gather current builder elements (form elements or slide elements for survey/quiz)
const currentBuilderElements = computed(() => {
  if (store.formId) {
    return store.elements || [];
  }
  // For survey/quiz, flatten slideData arrays
  const slides = store.slides || [];
  return slides.flatMap((s: any) => s?.slideData || []);
});

const isFieldAlreadyInBuilder = (field: any): boolean => {
  if (!field) {
    return false;
  }
  // Compare by id/Id/_id first (custom fields uniqueness)
  const candidateId = field?.id || field?.Id || field?._id;
  if (candidateId) {
    const existsById = currentBuilderElements.value?.some((el: any) => {
      const existingId = el?.id || el?.Id || el?._id;
      return existingId && existingId === candidateId;
    });
    if (existsById) {
      return true;
    }
  }

  // Fallback: compare by model + tag/fieldKey for standard/company/contact parity
  const model = field?.model;
  const queryKey = field?.hiddenFieldQueryKey;
  const fieldKey = field?.fieldKey;

  return currentBuilderElements.value?.some((el: any) => {
    const sameModel = (el?.model || "contact") === model;
    if (!sameModel) {
      return false;
    }
    const matchesByTag = queryKey && el?.tag === queryKey;
    const matchesByFieldKey = fieldKey && el?.fieldKey === fieldKey;
    return Boolean(matchesByTag || matchesByFieldKey);
  });
};

const shouldDisable = (field: any): boolean => {
  // Disable contact address child fields and phone if group_address is present in builder
  const groupAddressPresent = currentBuilderElements.value?.some(
    (el: any) => el?.tag === "group_address"
  );
  if (groupAddressPresent) {
    const addrKeys = new Set([
      "address",
      "street_address",
      "city",
      "state",
      "country",
      "postal_code",
      "phone",
    ]);
    const keyCandidateRaw = (field?.hiddenFieldQueryKey ||
      (typeof field?.fieldKey === "string" ? field.fieldKey.split(".").pop() : "") ||
      (typeof field?.name === "string" ? field.name : "")) as string;
    const keyCandidate = keyCandidateRaw.toString().trim().toLowerCase().replace(/\s+/g, "_");
    if (field?.model === "contact" && addrKeys.has(keyCandidate)) {
      return true;
    }
  }

  // Disable if the field already exists in the builder (by id/tag/fieldKey)
  if (isFieldAlreadyInBuilder(field)) {
    return true;
  }
  return false;
};

const allowMove = (evt: any): boolean => {
  const item = evt?.draggedContext?.element;
  return !shouldDisable(item);
};

const selection = useFieldSelection();
const focusedFieldId = ref<string>("");
const panelRef = ref<HTMLElement | null>(null);
const searchInputRef = ref<any>(null);

const getItemId = (item: any): string => {
  if (!item) {
    return "";
  }
  return (item.id || item.Id || item._id || item.tag || "") as string;
};

const onToggleSelect = (item: any): void => {
  if (shouldDisable(item)) {
    return;
  }
  const id = getItemId(item);
  selection.toggle(id);
  focusedFieldId.value = id;
};

const getVisibleRowIds = (): string[] => {
  const root = panelRef.value;
  if (!root) {
    return [];
  }
  const nodes = root.querySelectorAll<HTMLElement>("[data-field-id]");
  return Array.from(nodes)
    .map((n) => n.dataset.fieldId || "")
    .filter((id) => id);
};

// Selectable subset — excludes disabled rows so shift-click ranges
// don't fill in fields that are already on the canvas / blocked.
const getSelectableRowIds = (): string[] => {
  const disabled = new Set<string>();
  for (const field of (customFieldsCollection.value as any[]) || []) {
    if (shouldDisable(field)) {
      const id = getItemId(field);
      if (id) {
        disabled.add(id);
      }
    }
  }
  return getVisibleRowIds().filter((id) => !disabled.has(id));
};

const onRowClick = (event: MouseEvent, item: any): void => {
  if (shouldDisable(item)) {
    return;
  }
  const id = getItemId(item);
  if (!id) {
    return;
  }
  selection.handleRowClick(
    id,
    {
      shiftKey: event.shiftKey,
      metaKey: event.metaKey,
      ctrlKey: event.ctrlKey,
    },
    getSelectableRowIds()
  );
  focusedFieldId.value = id;
};

// Resolve the start index for a bulk insert: right after the currently-active
// element on the canvas, or at the end when nothing is active.
// Handles form (store.elements), survey and quiz (store.slides[slideIdx].slideData).
type InsertTarget =
  | { kind: "form"; startIdx: number }
  | { kind: "survey"; slideIndex: number; startIdx: number };

const resolveActiveElementIndexInSlide = (
  slide: any,
  slideIndex: number
): number => {
  const slideData = (slide?.slideData as any[]) || [];
  const activeTuple = (store.currentActiveIndex as [any, any] | undefined) || [
    undefined,
    undefined,
  ];
  const activeSlideKey = activeTuple[0];
  const activeElemKey = activeTuple[1];
  // Match slide either by index (survey uses slide index) or by id (quiz uses slide id).
  const activeMatchesCurrent =
    activeSlideKey === slideIndex ||
    (slide && activeSlideKey === slide.id);
  if (!store.isAnyElementActive || !activeMatchesCurrent) {
    return -1;
  }
  if (
    typeof activeElemKey === "number" &&
    activeElemKey >= 0 &&
    activeElemKey < slideData.length
  ) {
    return activeElemKey;
  }
  if (activeElemKey !== undefined && activeElemKey !== null) {
    // Quiz stores element identity as `el.uuid` (from ChangeInSlideData /
    // bulkAddToSlide's quiz branch). Survey/form use numeric index or
    // id/Id/_id/tag. Match all forms so bulk-insert-after-active works
    // consistently.
    const idx = slideData.findIndex(
      (el: any) =>
        el?.id === activeElemKey ||
        el?.Id === activeElemKey ||
        el?._id === activeElemKey ||
        el?.tag === activeElemKey ||
        el?.uuid === activeElemKey ||
        el?.hiddenFieldQueryKey === activeElemKey
    );
    return idx;
  }
  return -1;
};

const resolveInsertTarget = (): InsertTarget | null => {
  if (store.formId) {
    const elements = (store.elements as any[]) || [];
    const active = store.currentCustomSidebarPreview;
    if (
      store.isAnyElementActive &&
      typeof active === "number" &&
      active >= 0 &&
      active < elements.length
    ) {
      return { kind: "form", startIdx: active + 1 };
    }
    return { kind: "form", startIdx: elements.length };
  }
  const slides = (store.slides as any[]) || [];
  // `currentSlideIndex` is a numeric index for survey but a slide UUID string
  // for quiz (set by QuizBuilderPreview + bulkAddToSlide's quiz branch).
  // Handle both so bulk-insert lands on the correct slide.
  let slideIndex = -1;
  const raw = store.currentSlideIndex as any;
  if (typeof raw === "number" && raw >= 0 && raw < slides.length) {
    slideIndex = raw;
  } else if (typeof raw === "string" && raw) {
    slideIndex = slides.findIndex((s: any) => s?.id === raw);
  }
  if (slideIndex < 0) {
    slideIndex = 0;
  }
  const slide = slides[slideIndex];
  if (!slide) {
    return null;
  }
  const slideData = (slide.slideData as any[]) || [];
  const activeIdx = resolveActiveElementIndexInSlide(slide, slideIndex);
  const startIdx =
    activeIdx >= 0 && activeIdx < slideData.length
      ? activeIdx + 1
      : slideData.length;
  return { kind: "survey", slideIndex, startIdx };
};

// Append a single focused field (keyboard Enter) to the builder.
const appendFieldToBuilder = (field: any): void => {
  if (!field) {
    return;
  }
  if (store.formId) {
    const elements = (store.elements as any[]) || [];
    if (typeof store.handleDropAndMove === "function") {
      store.handleDropAndMove(field, elements.length);
    }
    return;
  }
  // Survey/quiz go through the same target resolution as `bulkInsert`.
  // `currentSlideIndex` is a numeric index for survey but a slide uuid for
  // quiz, so a numeric-only check silently collapses every quiz insert onto
  // slide 0. `resolveInsertTarget` handles both forms.
  const target = resolveInsertTarget();
  if (!target || target.kind !== "survey") {
    return;
  }
  void surveyDragOps.bulkAddToSlide(
    [field],
    target.slideIndex,
    target.startIdx
  );
};

const bulkInsert = (): void => {
  const selectedSet = selection.selectedIds.value;
  if (selectedSet.size === 0) {
    return;
  }
  // Iterate the visible list to preserve on-screen order.
  const orderedFields: any[] = [];
  const consumed: string[] = [];
  for (const field of (customFieldsCollection.value as any[]) || []) {
    const id = getItemId(field);
    if (selectedSet.has(id) && !shouldDisable(field)) {
      orderedFields.push(field);
      consumed.push(id);
    }
  }
  if (orderedFields.length === 0) {
    return;
  }
  const target = resolveInsertTarget();
  if (!target) {
    return;
  }
  if (target.kind === "form") {
    let cursor = target.startIdx;
    for (const field of orderedFields) {
      if (typeof store.handleDropAndMove === "function") {
        store.handleDropAndMove(field, cursor);
      }
      cursor += 1;
    }
  } else {
    // Batched synchronous insert — single splice, folder resolved once.
    void surveyDragOps.bulkAddToSlide(
      orderedFields,
      target.slideIndex,
      target.startIdx
    );
  }
  selection.remove(consumed);
};

// ─── Multi-drag: drop selected group at the drop position ─────────────────
type BuilderLocation =
  | { kind: "form"; index: number }
  | { kind: "survey"; slideIndex: number; index: number };

// Identity we track for the currently-dragged sidebar field. `id` is the raw
// dataset id (usually the mongo id). `fieldKey` is also captured because
// `normalizeContactStandardFieldDrop` strips id/Id/_id and rewrites tag →
// stdDef.tag on contact standard fields, but leaves fieldKey intact.
// We deliberately do NOT match on hiddenFieldQueryKey: it's derived from the
// field name (`getConstructedQueryKey`) and collides between sidebar custom
// fields ("First Name" → "first_name") and canvas standard fields whose tag
// is the same string ("first_name"). Matching on it produces false positives
// for sidebar customs whose name happens to align with a standard tag.
type DragIdentity = {
  id: string;
  fieldKey?: string;
};

const identityMatches = (element: any, identity: DragIdentity): boolean => {
  if (!element || !identity?.id) {
    return false;
  }
  if (
    element.Id === identity.id ||
    element.id === identity.id ||
    element._id === identity.id ||
    element.tag === identity.id
  ) {
    return true;
  }
  if (identity.fieldKey && element.fieldKey === identity.fieldKey) {
    return true;
  }
  return false;
};

const locateFieldInBuilder = (identity: DragIdentity): BuilderLocation | null => {
  if (store.formId) {
    const elements = (store.elements as any[]) || [];
    const i = elements.findIndex((el) => identityMatches(el, identity));
    return i === -1 ? null : { kind: "form", index: i };
  }
  const slides = (store.slides as any[]) || [];
  for (let s = 0; s < slides.length; s++) {
    const data = (slides[s]?.slideData as any[]) || [];
    const i = data.findIndex((el) => identityMatches(el, identity));
    if (i !== -1) {
      return { kind: "survey", slideIndex: s, index: i };
    }
  }
  return null;
};

const waitForFieldInBuilder = (
  identity: DragIdentity,
  timeoutMs = 2000
): Promise<BuilderLocation | null> => {
  const initial = locateFieldInBuilder(identity);
  if (initial) {
    return Promise.resolve(initial);
  }
  return new Promise((resolve) => {
    let done = false;
    const stop = watch(
      () => {
        if (store.formId) {
          return ((store.elements as any[]) || []).length;
        }
        return ((store.slides as any[]) || []).reduce(
          (acc: number, s: any) => acc + ((s?.slideData as any[])?.length || 0),
          0
        );
      },
      () => {
        if (done) {
          return;
        }
        const loc = locateFieldInBuilder(identity);
        if (loc) {
          done = true;
          stop();
          resolve(loc);
        }
      },
      { flush: "post" }
    );
    setTimeout(() => {
      if (!done) {
        done = true;
        stop();
        resolve(null);
      }
    }, timeoutMs);
  });
};

let dragSnapshotIds: string[] = [];
let dragOriginFieldId = "";
let dragOriginIdentity: DragIdentity | null = null;

// Simple pill matching the original main-branch row visual (single-field drag).
// Multi-field drag stacks two faded cards behind the pill and adds a "+N" badge.
const buildDragGhost = (label: string, count: number): HTMLElement => {
  const wrap = document.createElement("div");
  wrap.style.cssText = [
    "position: fixed",
    "top: -1000px",
    "left: -1000px",
    "z-index: 9999",
    "padding-right: 6px",
    "padding-bottom: 6px",
    "pointer-events: none",
  ].join(";");

  const multi = count > 1;

  if (multi) {
    const baseBackCard = [
      "position: absolute",
      "background: #ffffff",
      "border: 1px solid #d0d5dd",
      "border-radius: 5px",
      "width: 100%",
      "height: 100%",
      "top: 0",
      "left: 0",
      "box-shadow: 0 2px 4px rgba(16,24,40,0.06)",
    ].join(";");
    const back = document.createElement("div");
    back.style.cssText = `${baseBackCard};transform:translate(6px,6px);opacity:0.55;`;
    wrap.appendChild(back);
    const mid = document.createElement("div");
    mid.style.cssText = `${baseBackCard};transform:translate(3px,3px);opacity:0.8;`;
    wrap.appendChild(mid);
  }

  // Top card — matches main's `.custom-item` visual (white bg, 1px #d0d5dd
  // border, 5px radius, 10x15 padding, 14/600 Inter, subtle hover shadow).
  const top = document.createElement("div");
  top.style.cssText = [
    "position: relative",
    "display: inline-flex",
    "align-items: center",
    "justify-content: space-between",
    "gap: 12px",
    "padding: 10px 15px",
    "background: #ffffff",
    "border: 1px solid #d0d5dd",
    "border-radius: 5px",
    "box-shadow: 0 4px 8px -2px rgba(16,24,40,0.10), 0 2px 4px -2px rgba(16,24,40,0.06)",
    "font: 600 14px/20px Inter, system-ui, sans-serif",
    "color: #101828",
    "min-width: 160px",
    "max-width: 280px",
    "white-space: nowrap",
  ].join(";");

  const labelEl = document.createElement("span");
  labelEl.style.cssText =
    "flex:0 1 auto;min-width:0;overflow:hidden;text-overflow:ellipsis;max-width:220px;";
  labelEl.textContent = label;
  top.appendChild(labelEl);

  if (multi) {
    const badge = document.createElement("span");
    badge.style.cssText = [
      "flex: 0 0 auto",
      "display: inline-flex",
      "align-items: center",
      "justify-content: center",
      "min-width: 22px",
      "height: 20px",
      "padding: 0 7px",
      "background: #155EEF",
      "color: #ffffff",
      "border-radius: 10px",
      "font: 700 11px/1 Inter, sans-serif",
      "letter-spacing: 0.02em",
    ].join(";");
    badge.textContent = `+${count - 1}`;
    top.appendChild(badge);
  }
  wrap.appendChild(top);

  document.body.appendChild(wrap);
  // Browser rasterizes the drag image synchronously while we hold setDragImage,
  // so it's safe to remove on the next macrotask.
  setTimeout(() => wrap.remove(), 0);
  return wrap;
};

const sortableSetData = (
  dataTransfer: DataTransfer,
  dragEl: HTMLElement
): void => {
  // Preserve Sortable's default text payload so the drop target still sees it.
  try {
    dataTransfer.setData("Text", dragEl?.textContent || "");
  } catch (err) {
    // Some browsers throw on certain MIME types — drop still works with our
    // custom drag image below, so we swallow but log for diagnostics.
    console.debug("[SidebarCustomFields] dataTransfer.setData failed", err);
  }
  const id = (dragEl?.dataset?.fieldId || "") as string;
  if (!id) {
    return;
  }
  // Count only currently visible, non-disabled selected fields.
  let count = 0;
  let dragged: any = null;
  const isPartOfSelection =
    selection.isSelected(id) && selection.selectionCount.value > 1;
  if (isPartOfSelection) {
    for (const field of customFieldsCollection.value as any[]) {
      if (selection.isSelected(getItemId(field)) && !shouldDisable(field)) {
        count += 1;
        if (getItemId(field) === id) {
          dragged = field;
        }
      }
    }
  }
  if (count <= 1) {
    // Single-field drag: resolve the dragged field for the label.
    dragged =
      dragged ||
      (customFieldsCollection.value as any[]).find(
        (f: any) => getItemId(f) === id
      );
    count = 1;
  }
  const label = (dragged?.name as string) || id;
  const ghost = buildDragGhost(label, count);
  try {
    dataTransfer.setDragImage(ghost, 16, 16);
  } catch (err) {
    // Feature-test failure shouldn't block the drag; the browser will fall
    // back to its default drag image.
    console.debug("[SidebarCustomFields] setDragImage failed", err);
  }
};

const handleDragStart = (sortableEvent: any): void => {
  const draggedId = (sortableEvent?.item?.dataset?.fieldId || "") as string;
  dragOriginFieldId = draggedId;
  if (!draggedId) {
    dragSnapshotIds = [];
    dragOriginIdentity = null;
    return;
  }
  // Capture the source item's fieldKey — it survives the standard-field
  // normalize step that strips id from the canvas element. We don't capture
  // hiddenFieldQueryKey because it collides across custom/standard pairs.
  const sourceItem = (customFieldsCollection.value as any[])?.find(
    (f) => getItemId(f) === draggedId
  );
  dragOriginIdentity = {
    id: draggedId,
    fieldKey: sourceItem?.fieldKey,
  };
  if (selection.isSelected(draggedId) && selection.selectionCount.value > 1) {
    // Preserve the visible order of selected fields in the current type.
    const orderedSelected: string[] = [];
    for (const field of customFieldsCollection.value as any[]) {
      const id = getItemId(field);
      if (selection.isSelected(id) && !shouldDisable(field)) {
        orderedSelected.push(id);
      }
    }
    dragSnapshotIds = orderedSelected.length > 0 ? orderedSelected : [draggedId];
  } else {
    dragSnapshotIds = [draggedId];
  }
};

const handleDragEnd = async (sortableEvent: any): Promise<void> => {
  const evt = sortableEvent || {};
  const droppedToOtherList = Boolean(
    evt.from && evt.to && evt.from !== evt.to
  );
  const snapshot = dragSnapshotIds.slice();
  const draggedId = dragOriginFieldId;
  const identity = dragOriginIdentity;
  // Always reset state before any await to avoid stale captures.
  dragSnapshotIds = [];
  dragOriginFieldId = "";
  dragOriginIdentity = null;
  if (!droppedToOtherList || !draggedId) {
    return;
  }

  // The dragged field is now on the canvas — remove it from the selection
  // immediately so the bar reflects reality even if the batch below is
  // skipped or times out.
  if (selection.isSelected(draggedId)) {
    selection.remove([draggedId]);
  }

  // Single-field drag: nothing more to do.
  if (snapshot.length <= 1) {
    return;
  }

  // Snapshot the remaining selected ids for the multi-drag remainder.
  const remainingIds = snapshot.filter((id) => id !== draggedId);
  // Always drop the remaining ids from selection at the end of this flow,
  // regardless of whether the batch actually inserts them — otherwise the
  // bar lingers when the drop is rejected or the field lookup times out.
  const clearRemainingFromSelection = (): void => {
    if (remainingIds.length > 0) {
      selection.remove(remainingIds);
    }
  };

  const loc = await waitForFieldInBuilder(identity ?? { id: draggedId });
  if (!loc) {
    clearRemainingFromSelection();
    return;
  }

  // Collect the remaining selected fields in visible list order.
  // shouldDisable is re-checked because items could have been resolved /
  // raced between drag start and drop.
  const remainingFields: any[] = [];
  const remainingOrder = new Set(remainingIds);
  for (const field of (customFieldsCollection.value as any[]) || []) {
    const id = getItemId(field);
    if (remainingOrder.has(id) && !shouldDisable(field)) {
      remainingFields.push(field);
    }
  }
  if (remainingFields.length > 0) {
    if (loc.kind === "form") {
      let cursor = loc.index + 1;
      for (const field of remainingFields) {
        if (typeof store.handleDropAndMove === "function") {
          store.handleDropAndMove(field, cursor);
        }
        cursor += 1;
      }
    } else {
      void surveyDragOps.bulkAddToSlide(
        remainingFields,
        loc.slideIndex,
        loc.index + 1
      );
    }
  }
  clearRemainingFromSelection();
};

const isEditableTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  const tag = target.tagName.toLowerCase();
  return tag === "input" || tag === "textarea" || target.isContentEditable;
};

const scrollFocusedIntoView = (id: string): void => {
  requestAnimationFrame(() => {
    const root = panelRef.value;
    if (!root || !id) {
      return;
    }
    const el = root.querySelector<HTMLElement>(`[data-field-id="${CSS.escape(id)}"]`);
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  });
};

const focusSearchInput = (): void => {
  const refValue: any = searchInputRef.value;
  if (refValue && typeof refValue.focus === "function") {
    refValue.focus();
    return;
  }
  const root = panelRef.value;
  const input = root?.querySelector<HTMLInputElement>(
    "#search-custom-field input, input#search-custom-field"
  );
  input?.focus();
};

// Keydown is bound to `window` so shortcuts like `/` and Escape still work
// while focus is on canvas fields. Every branch below MUST early-exit unless
// the sidebar panel actually owns the key context (focus is inside the panel
// or the panel is currently hovered), otherwise ArrowUp/Down would hijack
// scroll and Enter would append the last-focused sidebar field to the form
// from anywhere else in the builder.
const panelOwnsKeyContext = (): boolean => {
  const root = panelRef.value;
  if (!root) return false;
  const active = document.activeElement;
  if (active instanceof HTMLElement && root.contains(active)) return true;
  return Boolean(root.matches(":hover"));
};

const onKeydown = (event: KeyboardEvent): void => {
  if (!panelOwnsKeyContext()) {
    return;
  }
  const inEditable = isEditableTarget(event.target);

  if (event.key === "/" && !inEditable) {
    event.preventDefault();
    focusSearchInput();
    return;
  }

  if (event.key === "Escape") {
    if (selection.hasSelection.value) {
      selection.clear();
    }
    const active = document.activeElement;
    if (
      active instanceof HTMLElement &&
      panelRef.value &&
      panelRef.value.contains(active) &&
      (active.tagName.toLowerCase() === "input" ||
        active.tagName.toLowerCase() === "textarea")
    ) {
      active.blur();
    }
    return;
  }

  if (inEditable) {
    return;
  }

  const order = getVisibleRowIds();
  if (order.length === 0) {
    return;
  }

  if (event.key === "ArrowDown" || event.key === "ArrowUp") {
    event.preventDefault();
    const currentIdx = focusedFieldId.value
      ? order.indexOf(focusedFieldId.value)
      : -1;
    let nextIdx: number;
    if (event.key === "ArrowDown") {
      nextIdx = currentIdx === -1 ? 0 : Math.min(order.length - 1, currentIdx + 1);
    } else {
      nextIdx = currentIdx === -1 ? order.length - 1 : Math.max(0, currentIdx - 1);
    }
    const next = order[nextIdx];
    if (next) {
      focusedFieldId.value = next;
      scrollFocusedIntoView(next);
    }
    return;
  }

  if (event.key === "Enter" && focusedFieldId.value) {
    const field = customFieldsCollection.value.find(
      (f: any) => getItemId(f) === focusedFieldId.value
    );
    if (field && !shouldDisable(field)) {
      event.preventDefault();
      appendFieldToBuilder(field);
    }
    return;
  }

  if (event.key === " " && focusedFieldId.value) {
    const field = customFieldsCollection.value.find(
      (f: any) => getItemId(f) === focusedFieldId.value
    );
    if (field && !shouldDisable(field)) {
      event.preventDefault();
      selection.toggle(getItemId(field));
    }
  }
};

// Initialize on component mount
onMounted(() => {
  updateDropdownOptions();
  window.addEventListener("keydown", onKeydown);
});

onBeforeUnmount(() => {
  window.removeEventListener("keydown", onKeydown);
});

updateCustomFieldsOptions();

watch(
  () => store.isCustomObjectEnabled,
  () => {
    updateDropdownOptions();
  }
);

watch(
  () => store.customFields,
  () => {
    updateCustomFieldsOptions();
  }
);

// Selection belongs to the current object type. Switching to another type
// (Contact -> Opportunity, etc.) would leave stale ids in the bar — clear.
watch(customFieldsType, () => {
  selection.clear();
  focusedFieldId.value = "";
});
</script>

<template>
  <div ref="panelRef" class="custom-list-container">
    <div class="cf-control-row">
      <div class="cf-search">
        <SearchSmIcon class="cf-search__icon" aria-hidden="true" />
        <UIInput
          id="search-custom-field"
          ref="searchInputRef"
          v-model="searchTerm"
          type="text"
          class="cf-search__input"
          :placeholder="t('common.customFieldSearch')"
          :style="{
            '--n-height': '32px',
            '--n-border': '0',
            '--n-border-hover': '0',
            '--n-border-focus': '0',
            '--n-border-disabled': '0',
            '--n-box-shadow-focus': 'none',
            '--n-color': 'transparent',
            '--n-color-focus': 'transparent',
            '--n-color-disabled': 'transparent',
            '--n-padding-left': '0',
            '--n-padding-right': '0',
          }"
          @update:model-value="updateCustomFieldsOptions"
        />
      </div>
      <UISelect
        id="custom-field-type"
        v-model:value="customFieldsType"
        class="cf-object-pill"
        :options="customFieldsOptions"
        :consistent-menu-width="false"
        :menu-props="{ class: 'cf-object-menu' }"
        :style="{
          '--n-height': '28px',
          '--n-border': '0',
          '--n-border-hover': '0',
          '--n-border-active': '0',
          '--n-border-focus': '0',
          '--n-box-shadow-focus': 'none',
          '--n-box-shadow-active': 'none',
          '--n-box-shadow-hover': 'none',
          '--n-color': 'transparent',
          '--n-color-active': 'transparent',
          '--n-color-disabled': 'transparent',
          '--n-arrow-color': '#344054',
          '--n-arrow-color-disabled': '#98a2b3',
        }"
        @update:value="updateCustomFieldsOptions"
      />
      <span class="cf-divider" aria-hidden="true" />
      <button
        id="add-custom-field"
        type="button"
        class="cf-add-btn"
        :aria-label="t('common.add')"
        @click="openCustomFieldModal"
      >
        <PlusIcon class="h-4 w-4" />
      </button>
    </div>

    <div class="custom-list-scroll">
      <template v-if="hasNoFieldsToShow">
        <div v-if="isSearching" class="cf-empty">
          <div class="cf-empty__icon cf-empty__icon--circle">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
              aria-hidden="true"
            >
              <path d="M11 3a8 8 0 1 0 0 16 8 8 0 0 0 0-16" />
              <path d="m21 21-4.35-4.35" />
              <path d="m8.5 8.5 5 5" />
              <path d="m13.5 8.5-5 5" />
            </svg>
          </div>
          <div class="cf-empty__title">{{ t("common.noFieldsFound") }}</div>
          <div class="cf-empty__subtitle">
            {{ t("common.noFieldsMatchQuery", { query: searchTerm }) }}
          </div>
          <button type="button" class="cf-empty__action" @click="clearSearch">
            {{ t("common.clearSearch") }}
          </button>
        </div>
        <div v-else class="cf-empty">
          <div class="cf-empty__icon cf-empty__icon--circle">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
              aria-hidden="true"
            >
              <path d="M11 3a8 8 0 1 0 0 16 8 8 0 0 0 0-16" />
              <path d="m21 21-4.35-4.35" />
              <path d="m8.5 8.5 5 5" />
              <path d="m13.5 8.5-5 5" />
            </svg>
          </div>
          <div class="cf-empty__title">{{ t("common.noFieldsYet") }}</div>
          <div class="cf-empty__subtitle">
            {{ t("common.noFieldsForObject") }}
          </div>
          <button type="button" class="cf-empty__action" @click="openCustomFieldModal">
            {{ t("common.createField") }}
          </button>
        </div>
      </template>
      <UICollapse
        v-else
        :accordion="true"
        :arrow-placement="'left'"
        :default-expanded-names="defaultExpandedGroup"
      >
        <UICollapseItem
          v-for="group in groupedCustomFields"
          :id="group.name"
          :key="group.name"
          :title="`${group.name} (${group.fields.length})`"
          :name="group.name"
        >
          <VueDraggableNext
            :list="group.fields.slice(0, group.showCount)"
            :sort="false"
            ghost-class="sortable-ghost"
            :group="{ name: 'elements', pull: 'clone', put: false }"
            :move="allowMove"
            :set-data="sortableSetData"
            @start="handleDragStart"
            @end="handleDragEnd"
          >
            <div
              v-for="(item, index) in group.fields.slice(0, group.showCount)"
              :key="index"
              :data-field-id="getItemId(item)"
              class="cf-row-wrap"
            >
              <div
                class="custom-item"
                :class="{
                  'is-disabled': shouldDisable(item),
                  'is-checked': selection.isSelected(getItemId(item)),
                  'is-focused': focusedFieldId === getItemId(item),
                }"
                :aria-disabled="shouldDisable(item) ? 'true' : 'false'"
                @click="onRowClick($event, item)"
              >
                <div class="custom-item-check" @click.stop>
                  <UICheckbox
                    :id="`custom-field-check-${getItemId(item)}`"
                    :checked="selection.isSelected(getItemId(item))"
                    :disabled="shouldDisable(item)"
                    :style="{
                      '--n-size': '16px',
                      '--n-border-radius': '4px',
                      '--n-color-checked': '#155eef',
                      '--n-border-checked': '1px solid #155eef',
                      '--n-border': '1px solid #d0d5dd',
                      '--n-border-focus': '1px solid #155eef',
                      '--n-box-shadow-focus': 'none',
                    }"
                    @update:checked="onToggleSelect(item)"
                  />
                </div>
                <span class="type-icon" aria-hidden="true">
                  <component :is="getFieldTypeIcon(item.dataType)" class="h-4 w-4" />
                </span>
                <div class="custom-item-label">
                  <span :title="item.name">{{ truncate(item.name, { length: 30 }) }}</span>
                </div>
                <div v-if="item.fieldKey" class="custom-item-key" :title="item.fieldKey">
                  {{ item.fieldKey }}
                </div>
                <div class="icon-container">
                  <svg
                    class="drag-handle"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <circle cx="9" cy="6" r="1.5" />
                    <circle cx="15" cy="6" r="1.5" />
                    <circle cx="9" cy="12" r="1.5" />
                    <circle cx="15" cy="12" r="1.5" />
                    <circle cx="9" cy="18" r="1.5" />
                    <circle cx="15" cy="18" r="1.5" />
                  </svg>
                </div>
              </div>
            </div>
          </VueDraggableNext>
          <div v-if="group.fields.length > group.showCount" class="cf-show-more-wrap">
            <button
              id="show-more"
              type="button"
              class="cf-show-more"
              @click="showMore(group)"
            >
              {{ t("common.more") }} {{ group.fields.length - group.showCount }}
              <ChevronDownIcon class="h-3.5 w-3.5" />
            </button>
          </div>
        </UICollapseItem>
      </UICollapse>
    </div>

    <transition name="bulk-bar-fade">
      <div v-if="selection.hasSelection.value" class="bulk-insert-bar">
        <span class="bulk-insert-bar__count">
          {{ t("common.selectedCount", { count: selection.selectionCount.value }) }}
        </span>
        <div class="bulk-insert-bar__actions">
          <button
            id="bulk-clear"
            type="button"
            class="bulk-insert-bar__clear"
            @click="selection.clear()"
          >
            {{ t("common.clear") }}
          </button>
          <button
            id="bulk-insert"
            type="button"
            class="bulk-insert-bar__insert"
            @click="bulkInsert"
          >
            {{
              t(
                "common.addFieldWithCount",
                { count: selection.selectionCount.value },
                selection.selectionCount.value
              )
            }}
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<style scoped>
.custom-list-container {
  height: calc(100vh - 180px);
  min-height: 420px;
  display: flex;
  flex-direction: column;
  background: #ffffff;
}

/* ── Control row ────────────────────────────────────────────────────────── */
.cf-control-row {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 12px;
  border-bottom: 1px solid #eaecf0;
  background: #ffffff;
}

.cf-search {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 10px;
}

.cf-search__icon {
  flex: 0 0 16px;
  width: 16px;
  height: 16px;
  color: #98a2b3;
}

.cf-search__input {
  flex: 1 1 auto;
  min-width: 0;
}

/* Strip HighRise input chrome — search field is borderless in the design. */
.cf-search__input.n-input,
.cf-search__input.n-input--focus,
.cf-search__input.n-input--stateful {
  background: transparent !important;
  border: 0 !important;
  box-shadow: none !important;
}
.cf-search__input :deep(.n-input__border),
.cf-search__input :deep(.n-input__state-border) {
  border: 0 !important;
  box-shadow: none !important;
  display: none !important;
}
.cf-search__input :deep(.n-input-wrapper) {
  padding: 0 !important;
}
.cf-search__input :deep(.n-input__input) {
  background: transparent !important;
}
.cf-search__input :deep(.n-input__input-el),
.cf-search__input :deep(.n-input__input) {
  height: 28px;
  font-size: 13px;
  font-weight: 500;
  color: #101828;
  background: transparent !important;
  padding: 0 !important;
}
.cf-search__input :deep(.n-input__placeholder) {
  color: #98a2b3;
  font-size: 13px;
  font-weight: 500;
}

/* Object dropdown — text-only, no border/background, matches design exactly. */
.cf-object-pill {
  flex: 0 0 auto;
  width: auto;
  min-width: 0;
  --n-height: 28px !important;
}
.cf-object-pill :deep(.n-base-selection) {
  min-height: 28px !important;
  height: 28px !important;
  line-height: 28px !important;
  padding: 0 !important;
  border-radius: 4px !important;
  background: transparent !important;
  --n-border: 0 !important;
  --n-border-hover: 0 !important;
  --n-border-active: 0 !important;
  --n-border-focus: 0 !important;
  --n-box-shadow-focus: none !important;
  cursor: pointer;
}
.cf-object-pill :deep(.n-base-selection),
.cf-object-pill :deep(.n-base-selection):hover,
.cf-object-pill :deep(.n-base-selection):focus,
.cf-object-pill :deep(.n-base-selection):focus-visible,
.cf-object-pill :deep(.n-base-selection):focus-within,
.cf-object-pill :deep(.n-base-selection--active),
.cf-object-pill :deep(.n-base-selection--focus) {
  background: transparent !important;
  box-shadow: none !important;
  outline: none !important;
}
.cf-object-pill :deep(.n-base-selection-label:focus),
.cf-object-pill :deep(.n-base-selection-label:focus-visible) {
  outline: none !important;
  box-shadow: none !important;
}
.cf-object-pill :deep(.n-base-selection__border),
.cf-object-pill :deep(.n-base-selection__state-border) {
  border: 0 !important;
  box-shadow: none !important;
  background: transparent !important;
  display: none !important;
}
.cf-object-pill :deep(.n-base-selection-label) {
  height: 28px !important;
  line-height: 28px !important;
  padding: 0 20px 0 4px !important;
  background: transparent !important;
  font-size: 13px;
  font-weight: 500;
  color: #101828;
}
.cf-object-pill :deep(.n-base-selection-input) {
  height: 28px !important;
  line-height: 28px !important;
  padding: 0 !important;
  background: transparent !important;
  font-size: 13px;
  font-weight: 500;
  color: #101828;
}
.cf-object-pill :deep(.n-base-selection-input__content) {
  font-weight: 500;
}
.cf-object-pill :deep(.n-base-suffix) {
  right: 2px;
  color: #344054;
}
.cf-object-pill :deep(.n-base-suffix__arrow) {
  color: #344054;
}

.cf-divider {
  flex: 0 0 auto;
  width: 1px;
  height: 16px;
  background: #eaecf0;
  margin: 0 2px;
}

.cf-add-btn {
  flex: 0 0 auto;
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #155eef;
  color: #ffffff;
  border: 0;
  border-radius: 6px;
  cursor: pointer;
  transition: background 120ms ease-out;
}
.cf-add-btn:hover {
  background: #004eeb;
}
.cf-add-btn:focus-visible {
  outline: 2px solid #84caff;
  outline-offset: 2px;
}

/* ── Scroll region ──────────────────────────────────────────────────────── */
.custom-list-scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding-top: 8px;
  padding-bottom: v-bind("store.isNewBuilderLayoutEnabled ? '100px' : '0'");
}

/* ── Empty state ────────────────────────────────────────────────────────── */
.cf-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 48px 24px;
}
.cf-empty__icon {
  width: 40px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  background: #f2f4f7;
  color: #667085;
}
.cf-empty__icon > svg,
.cf-empty__icon > :deep(svg) {
  width: 18px;
  height: 18px;
  stroke-width: 1.8;
}
.cf-empty__icon--circle {
  border-radius: 9999px;
  color: #667085;
}
.cf-empty__title {
  margin-top: 12px;
  font-size: 14px;
  line-height: 20px;
  font-weight: 600;
  color: #101828;
}
.cf-empty__subtitle {
  margin-top: 4px;
  font-size: 13px;
  line-height: 18px;
  font-weight: 400;
  color: #475467;
  max-width: 240px;
}
.cf-empty__action {
  margin-top: 16px;
  height: 32px;
  padding: 0 12px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  background: #ffffff;
  font-size: 13px;
  font-weight: 600;
  color: #344054;
  cursor: pointer;
  box-shadow: 0 1px 2px 0 rgba(16, 24, 40, 0.05);
}
.cf-empty__action:hover {
  color: #101828;
  background: #f9fafb;
  border-color: #98a2b3;
}

/* ── Field row ──────────────────────────────────────────────────────────── */
.cf-row-wrap {
  padding: 0;
}

.custom-item {
  height: 32px;
  padding: 0 12px 0 16px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  font-weight: 400;
  line-height: 18px;
  color: #344054;
  cursor: all-scroll;
  background: #ffffff;
  border: 0;
  border-radius: 0;
  -webkit-user-select: none;
  -ms-user-select: none;
  user-select: none;
  transition: background-color 100ms ease-out;
}

.custom-item:hover {
  background: #f9fafb;
}

.custom-item.is-checked {
  background: #f5f8ff;
}

.custom-item.is-focused {
  background: #f5f8ff;
}

.custom-item.is-disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.custom-item-check {
  flex: 0 0 16px;
  width: 16px;
  height: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: default;
}
.custom-item-check :deep(.n-checkbox) {
  min-height: 16px !important;
  --n-size: 16px !important;
  --n-border-radius: 4px !important;
  --n-color-checked: #155eef !important;
  --n-border-checked: 1px solid #155eef !important;
  --n-border: 1px solid #d0d5dd !important;
  --n-border-focus: 1px solid #155eef !important;
  --n-box-shadow-focus: none !important;
}
.custom-item-check :deep(.n-checkbox-box) {
  width: 16px !important;
  height: 16px !important;
  border-radius: 4px !important;
}
.custom-item-check :deep(.n-checkbox-box__border) {
  border-radius: 4px !important;
}
.custom-item-check :deep(.n-checkbox--checked .n-checkbox-icon .check-icon),
.custom-item-check :deep(.n-checkbox--checked .n-checkbox-icon .check-icon path) {
  fill: #ffffff !important;
  color: #ffffff !important;
}

.type-icon {
  flex: 0 0 16px;
  width: 16px;
  height: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #667085;
}

.custom-item-label {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 13px;
  font-weight: 400;
  line-height: 18px;
  color: #344054;
  display: flex;
  align-items: center;
}
.custom-item-label > span:first-child {
  flex: 0 1 auto;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.custom-item-key {
  flex: 0 1 auto;
  max-width: 130px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 10.5px;
  font-weight: 400;
  color: #98a2b3;
  line-height: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: right;
}

.icon-container {
  flex: 0 0 16px;
  width: 16px;
  height: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #98a2b3;
}

/* ── Show more link ─────────────────────────────────────────────────────── */
.cf-show-more-wrap {
  display: flex;
  justify-content: center;
  padding: 6px 0 10px;
}
.cf-show-more {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: transparent;
  border: 0;
  padding: 4px 8px;
  font-size: 12px;
  font-weight: 600;
  color: #155eef;
  cursor: pointer;
  border-radius: 4px;
}
.cf-show-more:hover {
  background: #f5f8ff;
}

/* ── Bottom bulk-insert bar ─────────────────────────────────────────────── */
.bulk-insert-bar {
  flex: 0 0 auto;
  position: sticky;
  bottom: 0;
  padding: 12px 16px;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #ffffff;
  border-top: 1px solid #eaecf0;
  z-index: 11;
}

.bulk-insert-bar__count {
  font-size: 14px;
  line-height: 20px;
  font-weight: 600;
  color: #101828;
}

.bulk-insert-bar__actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.bulk-insert-bar__clear {
  background: transparent;
  border: 0;
  padding: 0 8px;
  height: 40px;
  font-size: 14px;
  font-weight: 500;
  color: #667085;
  cursor: pointer;
  border-radius: 6px;
}
.bulk-insert-bar__clear:hover {
  background: #f9fafb;
  color: #101828;
}

.bulk-insert-bar__insert {
  background: #155eef;
  color: #ffffff;
  border: 0;
  height: 40px;
  padding: 0 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 150ms ease-out;
}
.bulk-insert-bar__insert:hover {
  background: #004eeb;
}

.bulk-bar-fade-enter-active,
.bulk-bar-fade-leave-active {
  transition: transform 160ms ease-out, opacity 160ms ease-out;
}
.bulk-bar-fade-enter-from,
.bulk-bar-fade-leave-to {
  transform: translateY(8px);
  opacity: 0;
}
</style>
<style>
/* Custom Fields sidebar — flat accordion (no dividers), roomy padding. */
.custom-list-scroll .n-collapse {
  --n-item-margin: 0 !important;
}
.custom-list-scroll .n-collapse-item {
  border: 0 !important;
  margin-top: 0 !important;
  padding-top: 0 !important;
}
.custom-list-scroll .n-collapse-item + .n-collapse-item {
  border-top: 0 !important;
  margin-top: 8px !important;
  padding-top: 0 !important;
}
.custom-list-scroll .n-collapse-item__header {
  padding: 8px 16px !important;
  border: 0 !important;
}
.custom-list-scroll .n-collapse-item__content-wrapper {
  padding: 0 !important;
}
.custom-list-scroll .n-collapse-item__content-inner {
  padding-top: 0 !important;
}
.custom-list-scroll .n-collapse-item__header-main {
  color: #667085 !important;
  text-transform: uppercase;
  font-size: 11px !important;
  font-weight: 600 !important;
  letter-spacing: 0.04em;
  display: inline-flex !important;
  align-items: center !important;
  line-height: 16px !important;
}
.custom-list-scroll .n-collapse-item-arrow {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  line-height: 0 !important;
  color: #475467 !important;
}

/* Object dropdown menu — free width so option labels don't truncate. */
.cf-object-menu {
  min-width: 200px !important;
}
.cf-object-menu .n-base-select-option {
  font-size: 13px !important;
  font-weight: 500 !important;
  color: #101828 !important;
}
</style>
